/*! custom-neo.js — LeGeekShop × Wayne Outfitters
   Optimisations Lighthouse : LCP/TBT/CLS, lazy images/iframes, 3P defer, lazy sections. */

(() => {
  "use strict";

  /* ------------------ Helpers robustes ------------------ */
  const on = (t, e, o, a = { passive: true }) => t.addEventListener(e, o, a);
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  const raf = (fn) => (window.requestAnimationFrame || setTimeout)(fn, 0);
  const idle = (fn, timeout = 1500) => {
    if ("requestIdleCallback" in window) return window.requestIdleCallback(fn, { timeout });
    return setTimeout(fn, timeout);
  };

  const whenInteractive = (fn) => {
    if (document.readyState === "interactive" || document.readyState === "complete") fn();
    else on(document, "DOMContentLoaded", fn, { passive: true });
  };

  const onVisible = (el, cb, rootMargin = "200px") => {
    if (!("IntersectionObserver" in window)) return cb();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          io.unobserve(e.target);
          cb(e.target);
        }
      });
    }, { rootMargin, threshold: 0.01 });
    io.observe(el);
  };

  /* ------------------ 1) Priorité LCP (slideshow + PDP) ------------------ */
  const boostLCP = () => {
    const lcpCandidates = [
      ".slideshow picture img",
      ".slideshow img",
      ".product-gallery img[loading]",
      ".Product__Gallery img",
      ".product__media img",
      ".gallery__image img",
      ".product-media img",
      ".media img"
    ];
    let lcp = null;
    for (const s of lcpCandidates) {
      lcp = $(s);
      if (lcp) break;
    }
    if (!lcp || lcp.closest(".product-card")) return;

    try {
      lcp.setAttribute("fetchpriority", "high");
      // Aide à la stabilité (CLS) si width/height manquent
      if (!lcp.getAttribute("width") || !lcp.getAttribute("height")) {
        const r = lcp.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          lcp.setAttribute("width", Math.round(r.width));
          lcp.setAttribute("height", Math.round(r.height));
        }
      }
      // decoding async
      lcp.setAttribute("decoding", "async");
      // s’assurer qu’elle n’est pas lazy
      lcp.removeAttribute("loading");
    } catch (e) { /* noop */ }
  };

  /* ------------------ 2) Lazyload images non-critiques ------------------ */
  const LAZY_EXTERNAL_RE = /\/kf\/|aliexpress-media\.com|ae-pic-/i;

  const tuneExternalImage = (img) => {
    const src = img.currentSrc || img.src || img.getAttribute("src") || "";
    const srcset = img.getAttribute("srcset") || "";
    if (!LAZY_EXTERNAL_RE.test(src) && !LAZY_EXTERNAL_RE.test(srcset)) return;
    // lgk-perf-boot gère le vrai defer data-src ; ici on renforce seulement
    img.loading = "lazy";
    img.decoding = "async";
    img.setAttribute("fetchpriority", "low");
  };

  const lazyAllImages = () => {
    const lcpImg = $(".slideshow picture img, .slideshow img, .product-gallery img, .product__media img");
    $$("img").forEach((img) => {
      if (lcpImg && img === lcpImg) return;
      tuneExternalImage(img);
      if (img.closest(".product-card") || img.closest(".product-list") || img.closest(".scroll-carousel")) {
        if (!img.hasAttribute("loading")) img.setAttribute("loading", "lazy");
        if (!img.hasAttribute("fetchpriority")) img.setAttribute("fetchpriority", "low");
      }
      if (!img.closest("header")) {
        if (!img.hasAttribute("loading")) img.setAttribute("loading", "lazy");
        if (!img.hasAttribute("decoding")) img.setAttribute("decoding", "async");
      }
      if ((img.naturalWidth && img.naturalWidth <= 64) || /icon|logo/i.test(img.src)) {
        img.setAttribute("decoding", "async");
      }
    });
  };

  const observeDynamicImages = () => {
    if (!("MutationObserver" in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const img = e.target;
          io.unobserve(img);
          const dataSrc = img.getAttribute("data-src") || img.getAttribute("data-lazy-src");
          if (dataSrc && !img.src) img.src = dataSrc;
        });
      },
      { rootMargin: "300px", threshold: 0.01 },
    );

    const prep = (img) => {
      tuneExternalImage(img);
      const src = img.getAttribute("src") || "";
      if (LAZY_EXTERNAL_RE.test(src) && img.loading !== "eager") io.observe(img);
    };

    $$("img").forEach(prep);
    new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        (m.addedNodes || []).forEach((node) => {
          if (node.tagName === "IMG") prep(node);
          if (node.querySelectorAll) $$("img", node).forEach(prep);
        });
      });
    }).observe(document.documentElement, { subtree: true, childList: true });
  };

  /* ------------------ 3) Lazyload iframes (YouTube, maps…) ------------------ */
  const lazyIframes = () => {
    $$("iframe").forEach((f) => {
      if (!f.hasAttribute("loading")) f.setAttribute("loading", "lazy");
      if (!f.hasAttribute("referrerpolicy")) f.setAttribute("referrerpolicy", "no-referrer-when-downgrade");
    });
  };

  /* ------------------ 4) Déférer scripts tiers via data-attrs ------------------
     Usage dans theme.liquid ou templates :
     <script data-3p="idle" data-src="https://example.com/sdk.js"></script>
     <script data-3p="visible" data-src="https://example.com/chat.js" data-target="#footer"></script>
     <script data-3p="delay" data-src="https://example.com/ads.js" data-delay="4000"></script>
  --------------------------------------------------------------------------- */
  const loadScript = (src, attrs = {}) =>
    new Promise((res, rej) => {
      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
      s.onload = res; s.onerror = rej;
      document.head.appendChild(s);
    });

  const hydrate3P = () => {
    $$("script[data-3p]").forEach((stub) => {
      const mode = stub.getAttribute("data-3p");
      const src  = stub.getAttribute("data-src");
      if (!src) return;

      const doLoad = () => {
        loadScript(src, { async: "", crossorigin: stub.getAttribute("crossorigin") || "" })
          .catch(() => {/* ignore */});
      };

      switch (mode) {
        case "idle":
          idle(doLoad, parseInt(stub.getAttribute("data-timeout") || "1500", 10));
          break;
        case "delay":
          setTimeout(doLoad, parseInt(stub.getAttribute("data-delay") || "3000", 10));
          break;
        case "visible": {
          const targetSel = stub.getAttribute("data-target");
          const target = targetSel ? $(targetSel) : document.body;
          if (!target) { idle(doLoad); break; }
          onVisible(target, doLoad);
          break;
        }
        default: // fallback : idle
          idle(doLoad);
      }
    });
  };

  /* ------------------ 5) Lazy sections (reco, UGC, gros blocs) ------------------
     Marquer dans le HTML : <div data-lazy-section="#selectorToReveal">…</div>
     Le sélecteur pointant la section à afficher/charger (ex: recommendations).
  ------------------------------------------------------------------------------- */
  const initLazySections = () => {
    $$("[data-lazy-section]").forEach((holder) => {
      onVisible(holder, () => {
        const sel = holder.getAttribute("data-lazy-section");
        if (!sel) return;
        const target = $(sel) || holder;
        target.removeAttribute("hidden");
        target.classList.add("is-visible");
        // Option : déclencher un event pour init sliders/carrousels si besoin
        const ev = new CustomEvent("neo:lazy-section:show", { detail: { target }});
        document.dispatchEvent(ev);
      });
    });
  };

  /* ------------------ 6) Micro-optimisations TBT ------------------ */
  const sliceLongTasks = () => {
    idle(() => {
      $$("details").forEach((d) => {
        if (!d.hasAttribute("data-inited")) {
          d.setAttribute("data-inited", "true");
          // amélioration accessibilité mineure
          const s = $("summary", d);
          if (s && !s.getAttribute("role")) s.setAttribute("role", "gd-button");
        }
      });
    }, 1000);
  };

  /* ------------------ 7) Sélecteurs spécifiques Gotham (safe) ------------------ */
  const gothamTweaks = () => {
    const firstHeroImg = $(".slideshow picture img, .slideshow img");
    if (firstHeroImg) {
      firstHeroImg.removeAttribute("loading");
      firstHeroImg.setAttribute("fetchpriority", "high");
    }
    const firstGalleryImg = $(".product-gallery img, .product__media img, .media img");
    if (firstGalleryImg) firstGalleryImg.removeAttribute("loading");
  };

  /* ------------------ 8) Sections sous la ligne de flottaison (accueil) ------------------ */
  const lazyHomeSections = () => {
    if (!document.body.classList.contains("template-index")) return;
    const skipRe = /slideshow|lgk-wayne-home|lgk-gotham-airsoft|header|announcement/i;
    let keptAboveFold = 0;
    $$("#main > .shopify-section, main .shopify-section").forEach((section) => {
      const id = section.id || "";
      const cls = section.className || "";
      if (skipRe.test(id) || skipRe.test(cls) || section.querySelector(".slideshow")) return;
      // Première section visible au-dessus de la ligne = garder peinte
      if (keptAboveFold < 1 && section.offsetParent !== null && section.getBoundingClientRect().top < window.innerHeight) {
        keptAboveFold += 1;
        return;
      }
      section.style.contentVisibility = "auto";
      section.style.containIntrinsicSize = "auto 480px";
    });
  };

  /* ------------------ 9) Réduire le coût des iframes/widgets ------------------ */
  const deferWidgets = () => {
    $$("[id*='judgeme' i], .jdgm-widget").forEach((el) => {
      if (el.closest(".shopify-section--slideshow")) return;
      onVisible(el, () => el.classList.add("is-lazy-visible"), "300px");
    });
  };

  /* ------------------ 9b) Masquer Judge.me vide (bande blanche sous description) --- */
  const hideEmptyJudgeMe = () => {
    const markEmpty = (el) => {
      if (!el || !el.classList) return;
      const n =
        el.getAttribute("data-number-of-reviews") ||
        el.querySelector("[data-number-of-reviews]")?.getAttribute("data-number-of-reviews");
      const emptyState = el.getAttribute("data-empty-state") === "empty_widget";
      const hasRev = !!el.querySelector(".jdgm-rev, .jdgm-rev-widg__reviews .jdgm-rev");
      if ((n === "0" || emptyState) && !hasRev) {
        el.classList.add("lgk-jdgm-empty-hidden");
        const wrap = el.closest(".product-content-below-gallery__reviews");
        if (wrap) wrap.style.display = "none";
      }
    };
    $$(".product-content-below-gallery__reviews .jdgm-widget, #judgeme_product_reviews").forEach(markEmpty);
  };

  /* ------------------ Orchestration ------------------ */
  whenInteractive(() => {
    try { boostLCP(); } catch (e) {}
    try { gothamTweaks(); } catch (e) {}
    try { lazyAllImages(); } catch (e) {}
    try { observeDynamicImages(); } catch (e) {}
    try { lazyIframes(); } catch (e) {}
    try { lazyHomeSections(); } catch (e) {}
    try { deferWidgets(); } catch (e) {}
    try { hideEmptyJudgeMe(); } catch (e) {}
    try { initLazySections(); } catch (e) {}
    try { hydrate3P(); } catch (e) {}
    try { sliceLongTasks(); } catch (e) {}
    // Judge.me hydrate souvent après coup
    setTimeout(() => { try { hideEmptyJudgeMe(); } catch (e) {} }, 1200);
    setTimeout(() => { try { hideEmptyJudgeMe(); } catch (e) {} }, 3500);
  });

  // Sécurité : si l’image LCP switch après sélection variante, rebooster
  on(document, "change", (e) => {
    if (e.target && /variant|option/i.test(e.target.name || "")) {
      raf(() => boostLCP());
    }
  });

})();
